import { ErreurApi, appelApi, urlApi } from './api';

// ---------------------------------------------------------------------------
// Dépôt d'une vidéo par morceaux : replays (TF-390) et vidéos de leçon (TF-444).
// ---------------------------------------------------------------------------

/** 10 Mo : la taille de morceau que l'API accepte (`TAILLE_MORCEAU_OCTETS`). */
const TAILLE_MORCEAU = 10 * 1024 * 1024;
const ESSAIS_PAR_MORCEAU = 3;

export interface AdressesDepot {
  /** GET : `{ recus, attendus }`, d'où reprendre. */
  progression: string;
  /** PUT d'un morceau à partir de l'octet `debut`. */
  morceau: (debut: number) => string;
  /** POST une fois le fichier complet. */
  finalisation: string;
}

async function lireErreur(reponse: Response): Promise<ErreurApi> {
  const corps = (await reponse.json().catch(() => null)) as {
    erreur?: { code?: string; message?: string };
  } | null;
  return new ErreurApi(
    reponse.status,
    corps?.erreur?.code ?? 'ERREUR',
    corps?.erreur?.message ?? 'Le serveur a refusé le morceau.',
  );
}

/**
 * Envoie le fichier à partir de là où le serveur en est, morceau par morceau,
 * puis le finalise. Une coupure ne coûte que le morceau en cours : trois
 * essais, puis l'erreur remonte et « Reprendre » repart du dernier octet reçu.
 */
export async function envoyerParMorceaux<T>(
  adresses: AdressesDepot,
  fichier: File,
  onProgression: (recus: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  const relire = async () =>
    (await appelApi<{ recus: number; attendus: number }>(adresses.progression, signal ? { signal } : {})).recus;
  let recus = await relire();
  onProgression(recus);

  while (recus < fichier.size) {
    const morceau = fichier.slice(recus, Math.min(fichier.size, recus + TAILLE_MORCEAU));
    let essai = 0;
    for (;;) {
      try {
        const reponse = await fetch(urlApi(adresses.morceau(recus)), {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: morceau,
          signal,
        });
        if (reponse.status === 409) {
          // Le serveur en est ailleurs (morceau déjà reçu) : on se recale.
          recus = await relire();
          break;
        }
        if (!reponse.ok) throw await lireErreur(reponse);
        recus = ((await reponse.json()) as { recus: number }).recus;
        break;
      } catch (erreur) {
        if (signal?.aborted || erreur instanceof ErreurApi || essai >= ESSAIS_PAR_MORCEAU - 1) throw erreur;
        essai += 1;
        await new Promise((fin) => setTimeout(fin, 1000 * essai));
      }
    }
    onProgression(recus);
  }

  return appelApi<T>(adresses.finalisation, { methode: 'POST' });
}
